import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * <bundle-builder>: main product + chosen products. The + / - buttons keep the cart in sync:
 * + adds the item's default variant to the cart, - removes it. Tiers are display only (a Shopify discount applies the saving).
 */
class BundleBuilder extends HTMLElement {
  connectedCallback() {
    this.tiers = [
      { qty: +this.dataset.t1Qty, pct: +this.dataset.t1Percent, code: this.dataset.t1Code || '' },
      { qty: +this.dataset.tmQty, pct: +this.dataset.tmPercent, code: this.dataset.tmCode || '' },
      { qty: +this.dataset.t2Qty, pct: +this.dataset.t2Percent, code: this.dataset.t2Code || '' },
    ].filter((t) => t.qty > 0).sort((a, b) => a.qty - b.qty);

    this.items = [...this.querySelectorAll('[data-bundle-item]')];
    this.msg = this.querySelector('[data-bundle-msg]');
    this.cta = this.querySelector('[data-bundle-add]');
    this.err = this.querySelector('[data-bundle-error]');
    this.target = 0;
    this.busy = false;
    this.queued = false;
    this.timer = null;
    // what is currently in the cart for each item: { variantId, qty }
    this.items.forEach((it) => (it._synced = { variantId: 0, qty: 0 }));

    this.addEventListener('click', this.onClick);
    this.cta.addEventListener('click', this.onCta);
    document.addEventListener(ThemeEvents.variantUpdate, this.onVariant);
    document.addEventListener('bundle:select', this.onSelect);
    document.addEventListener(ThemeEvents.cartUpdate, this.onExternalCart);
    this.render();
    this.loadCart(true);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.variantUpdate, this.onVariant);
    document.removeEventListener('bundle:select', this.onSelect);
    document.removeEventListener(ThemeEvents.cartUpdate, this.onExternalCart);
  }

  qty(item) { return +item.querySelector('[data-bundle-qty]').textContent || 0; }
  setQty(item, n) { item.querySelector('[data-bundle-qty]').textContent = String(Math.max(0, Math.min(99, n))); }

  /** Read the cart and mirror its quantities into the steppers. */
  async loadCart(initial) {
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      this.applyCart(cart, initial);
    } catch (_) {}
  }

  applyCart(cart, initial, mirror = true) {
    const lines = cart.items || [];
    for (const it of this.items) {
      const vid = +it.dataset.variantId;
      const line = lines.find((l) => l.variant_id === vid);
      const inCart = line ? line.quantity : 0;
      const wasSynced = it._synced.qty > 0;
      it._synced = { variantId: vid, qty: inCart };
      if (!mirror) continue;
      if (inCart > 0) this.setQty(it, inCart);
      else if (wasSynced || (!initial && it.dataset.main !== 'true')) this.setQty(it, 0);
      // first load and nothing in the cart: the main product stays pre-selected at 1 (not yet in the cart)
    }
    this.render();
  }

  onExternalCart = (e) => {
    if (e.detail?.data?.source === 'bundle-builder') return;
    this.loadCart(false);
  };

  onClick = (e) => {
    const plus = e.target.closest('[data-bundle-plus]');
    const minus = e.target.closest('[data-bundle-minus]');
    if (!plus && !minus) return;
    const item = e.target.closest('[data-bundle-item]');
    this.setQty(item, this.qty(item) + (plus ? 1 : -1));
    this.render();
    this.scheduleSync();
  };

  onVariant = (e) => {
    const v = e.detail?.resource;
    const main = this.querySelector('[data-main="true"]');
    if (!v || !main || !v.id) return;
    main.dataset.variantId = v.id;
    if (v.price != null) main.dataset.price = v.price;
    main.dataset.compare = v.compare_at_price ?? '';
    this.render();
    if (main._synced.qty > 0) this.scheduleSync();
  };

  onSelect = (e) => {
    this.target = +e.detail?.qty || 0;
    const main = this.querySelector('[data-main="true"]');
    if (main && this.qty(main) < 1 && main.dataset.available === 'true') this.setQty(main, 1);
    this.render();
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  tier(count) {
    let hit = null;
    for (const t of this.tiers) if (count >= t.qty) hit = t;
    return hit;
  }

  money(cents) {
    try {
      return new Intl.NumberFormat(this.dataset.locale || undefined, { style: 'currency', currency: this.dataset.currency }).format(cents / 100);
    } catch (_) { return (cents / 100).toFixed(2); }
  }

  render() {
    let count = 0, sum = 0;
    for (const it of this.items) {
      const q = this.qty(it);
      count += q;
      sum += q * (+it.dataset.price || 0);
      it.dataset.selected = q > 0 ? 'true' : 'false';
    }
    const tier = this.tier(count);
    const pct = tier ? tier.pct : 0;
    const total = Math.round(sum * (100 - pct) / 100);

    this.querySelectorAll('.bundle-tier').forEach((el, i) => {
      el.dataset.active = String(Boolean(tier) && this.tiers[i] === tier);
    });

    const next = this.tiers.find((t) => count < t.qty);
    let text;
    if (count === 0) text = this.dataset.labelNone;
    else if (next) text = `Add ${next.qty - count} more for ${next.pct}% off`;
    else text = `${this.tiers[this.tiers.length - 1].pct}% bundle saving unlocked`;
    if (this.target && count < this.target && count > 0) text = `Choose ${this.target - count} more to complete your ${this.target}-item bundle`;
    this.msg.textContent = text;

    this.querySelector('[data-bundle-count]').textContent = (this.dataset.labelItems || '[count] items').replace('[count]', count);
    const was = this.querySelector('[data-bundle-was]');
    const save = this.querySelector('[data-bundle-save]');
    was.hidden = save.hidden = !pct || !count;
    was.textContent = this.money(sum);
    save.textContent = `You save ${this.money(sum - total)} (${pct}%)`;
    this.querySelector('[data-bundle-total]').textContent = this.money(total);
    this.cta.disabled = count === 0;
    this.err.hidden = true;

    document.dispatchEvent(new CustomEvent('bundle:change', { detail: { count, pct, target: this.target } }));
  }

  scheduleSync() {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.sync(), 250);
  }

  /** Make the cart match the steppers (adds, changes or removes default variants). */
  async sync() {
    if (this.busy) { this.queued = true; return; }
    const updates = {};
    for (const it of this.items) {
      const want = this.qty(it);
      const vid = +it.dataset.variantId;
      const have = it._synced;
      if (have.qty > 0 && have.variantId && have.variantId !== vid) updates[have.variantId] = 0;
      if (want !== have.qty || have.variantId !== vid) updates[vid] = want;
    }
    if (!Object.keys(updates).length) return;

    this.busy = true;
    const sections = [...document.querySelectorAll('cart-items-component')].map((c) => c.dataset.sectionId).filter(Boolean).join(',');
    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates, sections }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || 'Could not update your bag');
      const cart = data.items ? data : await fetch('/cart.js').then((r) => r.json());
      this.applyCart(cart, false, false);
      this.dispatchEvent(new CartUpdateEvent(cart, this.id, { source: 'bundle-builder', itemCount: cart.item_count, sections: data.sections }));
    } catch (err) {
      this.err.textContent = err.message;
      this.err.hidden = false;
      await this.loadCart(false);
    } finally {
      this.busy = false;
      if (this.queued) { this.queued = false; this.sync(); }
    }
  }

  /** The button makes sure everything selected is in the cart, then opens the cart drawer. */
  onCta = async () => {
    this.cta.disabled = true;
    clearTimeout(this.timer);
    await this.sync();
    for (let i = 0; i < 20 && this.busy; i++) await new Promise((r) => setTimeout(r, 100));
    const tier = this.tier(this.items.reduce((n, it) => n + this.qty(it), 0));
    if (tier && tier.code) {
      await fetch(`/discount/${encodeURIComponent(tier.code)}`, { credentials: 'same-origin' }).catch(() => {});
    }
    document.querySelector('cart-drawer-component')?.open?.();
    this.render();
  };
}

if (!customElements.get('bundle-builder')) customElements.define('bundle-builder', BundleBuilder);
